import { useMutation } from '@apollo/client';
import { Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ButtonDock, MINI_BUTTON_DOCK_GAP, translateTemplateText } from '@focuslee/shared-mini-app';
import { CreateQuestMutation, CreateQuestMutationVariables, QuestDifficulty, RepeatCreateInput, UpdateQuestMutation, UpdateQuestMutationVariables } from '../client/generated/graphql';
import { createQuestCache, createQuestMutation } from '../client/mutations/createQuest';
import { updateQuestCache, updateQuestMutation } from '../client/mutations/updateQuest';
import { Page } from '../components/Page';
import { CategorySelect } from '../components/common/CategorySelect';
import { CommonIcon } from '../components/common/CommonIcon';
import { CustomInput } from '../components/common/CustomInput';
import { CustomSelect } from '../components/common/CustomSelect';
import { LiquidGlassChip } from '../components/common/LiquidGlassChip';
import { QuestDateSettings } from '../components/quest/QuestDateSettings';
import { QuestFocusSettings } from '../components/quest/QuestFocusSettings';
import { QuestCreateEditSkeleton } from '../components/quest/skeleton/QuestCreateEditSkeleton';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { useVoice } from '../contexts/VoiceContext';
import { AnalyticsEventType } from '../contexts/analyticsEvents';
import { filterOutTypeName } from '../helpers/apollo';
import { findCategoryByName } from '../helpers/category';
import { toDueString } from '../helpers/datetime';
import { mapDifficultyToXp } from '../helpers/experience';
import { openExternalQuest } from '../helpers/externalQuest';
import { getRepeatFromStr, getRepeatText } from '../helpers/quest';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useCachedGoalsWithFallback } from '../hooks/caching/goal';
import { useCachedQuestWithFallback } from '../hooks/caching/quest';
import { useAnalytics } from '../hooks/useAnalytics';

export const QuestCreateEditPage: FC = () => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const { id: questId, categoryId: preselectedCategoryId } = useParams<{ id?: string; categoryId?: string }>();
  const [searchParams] = useSearchParams();
  const voiceActionId = searchParams.get('voiceActionId');
  const preselectedDate = searchParams.get('selectedDate');
  const { pendingActions, onUpdateVoiceAction } = useVoice();
  const voiceAction = useMemo(
    () => (voiceActionId ? pendingActions.find((a) => a.id === voiceActionId) : null),
    [voiceActionId, pendingActions]
  );
  const { quest: editingQuest, loading: questLoading } = useCachedQuestWithFallback(questId);
  const { categories: allCategories, loading: categoriesLoading } = useCachedCategoriesWithFallback();
  const goalsEnabled = useFeatureFlag('goals');
  const { goals: allGoals, loading: goalsLoading } = useCachedGoalsWithFallback();
  const editingCategory = useMemo(
    () => allCategories.find((c) => c.id === editingQuest?.categoryId),
    [allCategories, editingQuest]
  );
  const categories = useMemo(() => {
    const nonSystem = allCategories.filter((c) => !c.system);
    // Keep the quest's own category selectable/visible even if it's a system category, so a
    // locked system quest still shows its real category instead of a blank select.
    if (editingCategory?.system && !nonSystem.some((c) => c.id === editingCategory.id))
      return [...nonSystem, editingCategory];
    return nonSystem;
  }, [allCategories, editingCategory]);
  const goals = allGoals.filter(g => g.enabled);
  const isExternalQuest = !!editingQuest?.external;
  const isSystemQuest = !!editingCategory?.system;
  const isFieldsLocked = !!questId && (isSystemQuest || isExternalQuest);
  
  const isLoading = questLoading || categoriesLoading || goalsLoading || (questId && !editingQuest);
  
  const [questName, setQuestName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [goalId, setGoalId] = useState<string>('');
  const [difficulty, setDifficulty] = useState<string>(QuestDifficulty.NoEffort);
  const [selectedFocusTime, setSelectedFocusTime] = useState<string>('');
  const [focusModeEnabled, setFocusModeEnabled] = useState(false);
  const [showFocusSettings, setShowFocusSettings] = useState(false);
  const [showDateSettings, setShowDateSettings] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(preselectedDate ? dayjs(preselectedDate).toDate() : null);
  const [repeat, setRepeat] = useState<RepeatCreateInput | null>(null);
  const isValid = !!categoryId && !!questName;
  const navigate = useNavigate();
  const { recordEvent } = useAnalytics();
  
  const [createQuest, { loading: isCreateLoading }] = useMutation<CreateQuestMutation, CreateQuestMutationVariables>(createQuestMutation, {
      update: createQuestCache
  });
  const [updateQuest, { loading: isUpdateLoading }] = useMutation<UpdateQuestMutation, UpdateQuestMutationVariables>(updateQuestMutation);

  const difficultyOptions = useMemo(() => Object.values(QuestDifficulty)
        .map(d => [d, mapDifficultyToXp(d)] as [QuestDifficulty, number])
        .sort((t1,t2) => t1[1] - t2[1])
        .map(entry => (
            { id: entry[0], label: `${t(`quest.difficulty.${entry[0]}`)} (+${entry[1]} XP)`, shortLabel: `+${entry[1]} XP` }
        )) || [], [t]);
  const goalOptions = useMemo(() => 
      [{ id: '', label: t('quest.noGoal') }, ...goals.map((g) => ({ id: g.id, label: g.name }))],
      [goals, t]);
  
  useEffect(() => {
    if (inputRef.current && !editingQuest) {
      inputRef.current.focus();
    }
  }, [inputRef, inputRef.current, editingQuest]);

  useEffect(() => {
    if (voiceActionId || questId) return;
    if (categories.length > 0 && !categoryId) {
      setCategoryId(preselectedCategoryId || categories[0].id);
    } else if (categories.length === 0) {
      setCategoryId('');
    }
  }, [categories, categoryId, preselectedCategoryId, voiceActionId, questId]);

  useEffect(() => {
    if (editingQuest) {
      setQuestName(translateTemplateText(t, editingQuest.templateId, 'name', editingQuest.name));
      setDescription(translateTemplateText(t, editingQuest.templateId, 'description', editingQuest.description ?? ''));
      setCategoryId(editingQuest.categoryId);
      setGoalId(editingQuest.goalId ?? '');
      setDifficulty(editingQuest.difficulty);
      setFocusModeEnabled(editingQuest.timing ?? false);
      setSelectedFocusTime(editingQuest.timedInterval?.toString() ?? '');
      setSelectedDate(editingQuest.dueDate ? new Date(editingQuest.dueDate) : null);
      setRepeat(editingQuest.repeat ? filterOutTypeName(editingQuest.repeat) : null);
    }
  }, [editingQuest, t]);

  useEffect(() => {
    if (!voiceActionId || !categories.length || editingQuest) return;
    const defaultCat = categories[0];
    if (voiceAction?.type === 'CREATE_QUEST') {
      setQuestName(voiceAction.questName);
      setDescription(voiceAction.description ?? '');
      const catId = voiceAction.categoryId
        ?? findCategoryByName(categories, voiceAction.categoryName ?? '')?.id
        ?? defaultCat?.id
        ?? '';
      setCategoryId(catId);
      setSelectedDate(voiceAction.dueDateIso ? new Date(voiceAction.dueDateIso) : null);
      setDifficulty(voiceAction.difficulty && Object.values(QuestDifficulty).includes(voiceAction.difficulty as QuestDifficulty) ? voiceAction.difficulty : QuestDifficulty.NoEffort);
      setFocusModeEnabled(voiceAction.focusModeEnabled ?? false);
      setSelectedFocusTime(voiceAction.timedIntervalMinutes ? String(voiceAction.timedIntervalMinutes) : '');
      const repeatFromQuery = voiceAction.repeatQuery ? getRepeatFromStr(voiceAction.repeatQuery)?.[0] : null;
      setRepeat(repeatFromQuery ? { ...repeatFromQuery, query: getRepeatText(repeatFromQuery) } : null);
    } else {
      setCategoryId(defaultCat?.id ?? '');
    }
  }, [voiceActionId, voiceAction, categories, editingQuest]);

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, []);

  const handleOpenQuest = useCallback(
    () => openExternalQuest(editingQuest?.external, editingQuest?.externalDeepLink),
    [editingQuest?.external, editingQuest?.externalDeepLink]
  );

  const handleCreate = useCallback(async () => {
    if (voiceActionId && voiceAction?.type === 'CREATE_QUEST') {
      if (!questName?.trim() || !categoryId) return;
      const cat = categories.find((c) => c.id === categoryId);
      const timedIntervalMinutes = parseInt(selectedFocusTime || '0', 10) || 0;
      onUpdateVoiceAction(voiceAction.id, {
        displayTitle: questName!,
        questName: questName!,
        description,
        categoryId,
        categoryName: cat?.name,
        dueDateIso: selectedDate ? selectedDate.toISOString() : undefined,
        difficulty,
        focusModeEnabled,
        timedIntervalMinutes: focusModeEnabled ? timedIntervalMinutes : undefined,
        repeatQuery: repeat?.query,
      });
      navigateBack();
      return;
    }

    if (!isValid || isCreateLoading || isUpdateLoading) return;

    const timedInterval = parseInt(selectedFocusTime ? selectedFocusTime : '0');
    const timerValueInMs = editingQuest?.timerValueInMs ?? 0;
    const commonVariables = {
      name: questName!,
      description: description ?? '',
      categoryId,
      difficulty: QuestDifficulty[difficulty as keyof typeof QuestDifficulty],
      repeat,
      dueDate: selectedDate ? selectedDate : null,
      timing: focusModeEnabled,
      timedInterval,
      timerValueInMs,
      goalId: goalId || null,
    };

    try {
      if (questId) {
        const oldCategoryId = editingQuest?.categoryId;
        await updateQuest({
          variables: { questId, ...commonVariables },
          update: (cache, result) => updateQuestCache(cache, result, oldCategoryId),
        });
        recordEvent(AnalyticsEventType.QUEST_UPDATED, { questId, categoryId });
      } else {
        const result = await createQuest({ variables: { ...commonVariables } });
        recordEvent(AnalyticsEventType.QUEST_CREATED, { questId: result.data?.createQuest?.id, categoryId });
      }
      navigateBack();
    } catch (error) {
      console.error("Error creating or updating quest:", error);
    }
  }, [isValid, isCreateLoading, isUpdateLoading, categoryId, questName, description, difficulty, goalId, focusModeEnabled, selectedFocusTime, repeat, selectedDate, questId, voiceActionId, voiceAction, categories, onUpdateVoiceAction, updateQuest, createQuest, editingQuest, recordEvent]);

  const isFocusSet = focusModeEnabled || (selectedFocusTime && parseInt(selectedFocusTime) > 0);

  const getFocusChipText = () => {
    const state = focusModeEnabled ? t('quest.focus.on') : t('quest.focus.off');
    if (!selectedFocusTime || parseInt(selectedFocusTime) === 0) {
      return state;
    }
    return `${state} (${selectedFocusTime}m)`;
  };

  const handleFocusClick = () => {
    setShowFocusSettings(true);
  };

  const handleSaveFocusSettings = (settings: { focusTime: string; enabled: boolean }) => {
    setSelectedFocusTime(settings.focusTime);
    setFocusModeEnabled(settings.enabled);
    setShowFocusSettings(false);
  };

  const handleCancelFocusSettings = () => {
    setShowFocusSettings(false);
  };

  const handleDateClick = () => {
    setShowDateSettings(true);
  };

  const handleSaveDateSettings = async (settings: { date: Date | null, repeat: RepeatCreateInput | null }) => {
    setSelectedDate(settings.date);
    setRepeat(settings.repeat);
    setShowDateSettings(false);
  };

  const handleCancelDateSettings = () => {
    setShowDateSettings(false);
  };

  const getDateChipText = () => {
    if (selectedDate) return toDueString(dayjs(selectedDate), false);
    return questId ? t('quest.noDueDate') : t('quest.date');
  };

  const isSaving = isCreateLoading || isUpdateLoading;
  const mainText = voiceActionId ? t('actions.update') : questId ? t('actions.save') : t('actions.create');

  if (isLoading)
    return <QuestCreateEditSkeleton isEdit={!!questId} />;
  
  if (showDateSettings) {
    return (
      <QuestDateSettings
        initialDate={selectedDate}
        initialRepeat={repeat}
        onSave={handleSaveDateSettings}
        onCancel={handleCancelDateSettings}
      />
    );
  }

  if (showFocusSettings) {
    return (
      <QuestFocusSettings
        initialSelectedTimeOption={selectedFocusTime}
        initialFocusModeEnabled={focusModeEnabled}
        onSave={handleSaveFocusSettings}
        onCancel={handleCancelFocusSettings}
      />
    );
  }

  return (
    <Page
      back={true}
      insetEnabled={false}
      footer={
        <Stack>
          {isExternalQuest && (
            <Stack direction="row" justifyContent="flex-end" sx={{ pb: 1, pr: `${MINI_BUTTON_DOCK_GAP}px` }}>
              <LiquidGlassChip
                icon={<CommonIcon icon="open_in_new" type="filled" color="var(--tg-theme-text-color)" size={18} />}
                label={t('quest.open')}
                onClick={handleOpenQuest}
              />
            </Stack>
          )}
          <ButtonDock
            secondary={{ text: t('actions.cancel'), onClick: navigateBack, navigation: 'back' }}
            main={{
              text: mainText,
              onClick: handleCreate,
              enabled: isValid && !isSaving,
              loading: isSaving,
              bgColor: getButtonThemeColors(isValid && !isSaving).backgroundColor,
            }}
          />
        </Stack>
      }
    >
      <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
        <Text>{voiceActionId ? t('quest.editAction') : questId ? t('quest.edit') : t('quest.create')}</Text>
      </Stack>
      <Stack height="100%" paddingLeft="10px" paddingRight="10px" paddingTop="4px">
        <CustomInput
          value={questName}
          onChange={(e) => setQuestName(e.target.value)}
          ref={inputRef}
          style={{ fontSize: '20px', lineHeight: '20px' }}
          placeholder={t('quest.name')}
          disabled={isFieldsLocked}
        />
        <CustomInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('quest.description')}
          disabled={isFieldsLocked}
        />
        <Stack direction="column" width="100%" rowGap="20px">
          <Stack direction="row" justifyContent="flex-start">
            <LiquidGlassChip
              icon={<CommonIcon icon="date_range" type="filled" color="var(--tg-theme-text-color)" size={20} />}
              iconAfter={repeat ? <CommonIcon icon="repeat" type="filled" color="var(--tg-theme-text-color)" size={20} /> : undefined}
              label={getDateChipText()}
              subLabel={repeat ? getRepeatText(repeat) : undefined}
              selected={!!(selectedDate || (repeat && repeat.query))}
              onClick={handleDateClick}
            />
          </Stack>

          <Stack flexWrap="wrap" direction="row" justifyContent="flex-start" columnGap="10px" rowGap="20px">
            <CategorySelect
              categoryId={categoryId}
              setCategoryId={setCategoryId}
              categories={categories}
              disabled={isFieldsLocked}
            />
            <LiquidGlassChip
              icon={<CommonIcon icon={focusModeEnabled ? 'timer' : 'timer_off'} type="filled" color="var(--tg-theme-text-color)" size={20} />}
              label={getFocusChipText()}
              selected={!!isFocusSet}
              onClick={handleFocusClick}
              disabled={isFieldsLocked}
            />
            <CustomSelect
              selectedId={difficulty}
              setSelectedId={setDifficulty}
              options={difficultyOptions}
              text={(d) => d.label}
              renderSelected={(d) => d ? d.shortLabel : t('quest.difficulty.label')}
              renderSelectedBefore={() => <CommonIcon icon="grading" type="filled" color="var(--tg-theme-text-color)" size={20} />}
              disabled={isFieldsLocked}
            />
            {goalsEnabled && <CustomSelect
              selectedId={goalId}
              setSelectedId={setGoalId}
              options={goalOptions}
              text={(g) => g.label}
              renderSelected={(g) => g && g.id !== '' ? g.label : t('quest.goal')}
              renderSelectedBefore={() => <CommonIcon icon="track_changes" type="filled" color="var(--tg-theme-text-color)" size={20} />}
              disabled={isFieldsLocked}
            />}
          </Stack>
        </Stack>
      </Stack>
    </Page>
  );
};
